(() => {
  const SELECTOR = '[data-cards-grid-slideshow]';

  class CardsGridSlideshow {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-cards-grid-viewport]');
      this.track = root.querySelector('[data-cards-grid-track]');
      this.slides = Array.from(root.querySelectorAll('[data-cards-grid-slide]'));
      this.previousButton = root.querySelector('[data-cards-grid-previous]');
      this.nextButton = root.querySelector('[data-cards-grid-next]');
      this.dotsContainer = root.querySelector('[data-cards-grid-dots]');
      this.status = root.querySelector('[data-cards-grid-status]');
      this.autoplayEnabled = root.dataset.autoplay === 'true';
      this.autoplaySpeed = Number.parseInt(root.dataset.autoplaySpeed || '5000', 10);
      this.currentIndex = 0;
      this.maxIndex = 0;
      this.autoplayTimer = null;
      this.resizeTimer = null;
      this.scrollFrame = null;
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.isVisible = true;

      if (!this.track || this.slides.length === 0) return;

      this.bindEvents();
      this.observeSize();
      this.observeVisibility();
      this.refresh();

      if (document.fonts?.ready) {
        document.fonts.ready.then(() => this.refresh());
      }

      this.root.querySelectorAll('img').forEach((image) => {
        if (!image.complete) {
          image.addEventListener('load', () => this.refresh(), { once: true });
        }
      });

      this.startAutoplay();
      this.root.dataset.cardsGridInitialized = 'true';
    }

    bindEvents() {
      this.previousButton?.addEventListener('click', () => {
        this.goTo(this.currentIndex - 1);
      });

      this.nextButton?.addEventListener('click', () => {
        this.goTo(this.currentIndex + 1);
      });

      this.track.addEventListener(
        'scroll',
        () => {
          if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = requestAnimationFrame(() => this.syncFromScroll());
        },
        { passive: true }
      );

      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.currentIndex - 1);
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.currentIndex + 1);
        }

        if (event.key === 'Home') {
          event.preventDefault();
          this.goTo(0);
        }

        if (event.key === 'End') {
          event.preventDefault();
          this.goTo(this.maxIndex);
        }
      });

      window.addEventListener('resize', () => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.refresh(), 120);
      });

      this.root.addEventListener('mouseenter', () => this.stopAutoplay());
      this.root.addEventListener('mouseleave', () => this.startAutoplay());
      this.root.addEventListener('focusin', () => this.stopAutoplay());
      this.root.addEventListener('focusout', () => this.startAutoplay());

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      });
    }

    observeSize() {
      if (!('ResizeObserver' in window)) return;

      this.resizeObserver = new ResizeObserver(() => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.refresh(), 60);
      });

      this.resizeObserver.observe(this.viewport || this.root);
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          this.isVisible = Boolean(entry?.isIntersecting);

          if (this.isVisible) this.startAutoplay();
          else this.stopAutoplay();
        },
        { threshold: 0.2 }
      );

      this.intersectionObserver.observe(this.root);
    }

    getPerView() {
      let value = this.root.dataset.slidesMobile || '1';

      if (window.matchMedia('(min-width: 990px)').matches) {
        value = this.root.dataset.slidesDesktop || value;
      } else if (window.matchMedia('(min-width: 750px)').matches) {
        value = this.root.dataset.slidesTablet || value;
      }

      return Math.max(1, Number.parseInt(value, 10));
    }

    getStep() {
      const firstSlide = this.slides[0];
      if (!firstSlide) return this.track.clientWidth;

      const styles = getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0');

      return firstSlide.getBoundingClientRect().width + gap;
    }

    refresh() {
      const perView = Math.min(this.getPerView(), this.slides.length);
      this.maxIndex = Math.max(0, this.slides.length - perView);
      this.currentIndex = Math.min(this.currentIndex, this.maxIndex);

      this.buildDots();
      this.updateControls();
      this.goTo(this.currentIndex, false);
    }

    buildDots() {
      if (!this.dotsContainer) return;

      this.dotsContainer.replaceChildren();
      const pageCount = this.maxIndex + 1;
      this.dotsContainer.hidden = pageCount <= 1;

      for (let index = 0; index < pageCount; index += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cards-grid__slider-dot';
        dot.dataset.index = String(index);
        dot.setAttribute(
          'aria-label',
          `Gehe zu Slideshow-Position ${index + 1} von ${pageCount}`
        );
        dot.addEventListener('click', () => this.goTo(index));
        this.dotsContainer.appendChild(dot);
      }
    }

    goTo(index, smooth = true) {
      if (this.maxIndex <= 0) {
        this.currentIndex = 0;
        this.updateControls();
        return;
      }

      let targetIndex = index;

      if (targetIndex < 0) targetIndex = this.maxIndex;
      if (targetIndex > this.maxIndex) targetIndex = 0;

      const targetSlide = this.slides[targetIndex];
      if (!targetSlide) return;

      this.currentIndex = targetIndex;

      this.track.scrollTo({
        left: targetSlide.offsetLeft,
        behavior:
          smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'smooth'
            : 'auto',
      });

      this.updateControls();
      this.restartAutoplay();
    }

    syncFromScroll() {
      const step = this.getStep();
      if (!step) return;

      const nextIndex = Math.min(
        this.maxIndex,
        Math.max(0, Math.round(this.track.scrollLeft / step))
      );

      if (nextIndex !== this.currentIndex) {
        this.currentIndex = nextIndex;
        this.updateControls();
      }
    }

    updateControls() {
      const hasMultiplePositions = this.maxIndex > 0;
      const perView = Math.min(this.getPerView(), this.slides.length);

      if (this.previousButton) this.previousButton.hidden = !hasMultiplePositions;
      if (this.nextButton) this.nextButton.hidden = !hasMultiplePositions;

      this.dotsContainer
        ?.querySelectorAll('.cards-grid__slider-dot')
        .forEach((dot, index) => {
          dot.setAttribute(
            'aria-current',
            index === this.currentIndex ? 'true' : 'false'
          );
        });

      this.slides.forEach((slide, index) => {
        const isVisible =
          index >= this.currentIndex &&
          index < this.currentIndex + perView;

        slide.setAttribute('aria-hidden', String(!isVisible));
      });

      if (this.status) {
        this.status.textContent =
          `Slideshow-Position ${this.currentIndex + 1} von ${this.maxIndex + 1}`;
      }
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.maxIndex <= 0) return;
      if (!this.isVisible || document.hidden) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.stopAutoplay();

      this.autoplayTimer = window.setInterval(() => {
        this.goTo(this.currentIndex + 1);
      }, Math.max(3000, this.autoplaySpeed));
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;

      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    restartAutoplay() {
      if (!this.autoplayEnabled) return;

      this.stopAutoplay();
      this.startAutoplay();
    }

    destroy() {
      this.stopAutoplay();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.root.removeAttribute('data-cards-grid-initialized');
    }
  }

  const instances = new WeakMap();

  const init = (scope = document) => {
    const roots = [];

    if (scope.matches?.(SELECTOR)) roots.push(scope);
    roots.push(...(scope.querySelectorAll?.(SELECTOR) || []));

    roots.forEach((root) => {
      if (
        instances.has(root) ||
        root.dataset.cardsGridInitialized === 'true'
      ) {
        return;
      }

      const instance = new CardsGridSlideshow(root);
      instances.set(root, instance);
    });
  };

  if (!window.CardsGridSlideshowApp) {
    window.CardsGridSlideshowApp = { init, instances };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => init(document), {
        once: true,
      });
    } else {
      init(document);
    }

    document.addEventListener('shopify:section:load', (event) => {
      init(event.target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const root = event.target.matches?.(SELECTOR)
        ? event.target
        : event.target.querySelector?.(SELECTOR);

      instances.get(root)?.destroy();
    });

    document.addEventListener('shopify:section:select', (event) => {
      const root = event.target.matches?.(SELECTOR)
        ? event.target
        : event.target.querySelector?.(SELECTOR);

      instances.get(root)?.stopAutoplay();
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      const root = event.target.matches?.(SELECTOR)
        ? event.target
        : event.target.querySelector?.(SELECTOR);

      instances.get(root)?.startAutoplay();
    });

    document.addEventListener('shopify:block:select', (event) => {
      const slide = event.target.closest?.('[data-cards-grid-slide]');
      const root = slide?.closest?.(SELECTOR);
      const instance = root ? instances.get(root) : null;

      if (!instance || !slide) return;

      const index = instance.slides.indexOf(slide);
      instance.stopAutoplay();
      instance.goTo(Math.min(index, instance.maxIndex));
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      const root = event.target.closest?.(SELECTOR);
      instances.get(root)?.startAutoplay();
    });
  } else {
    window.CardsGridSlideshowApp.init(document);
  }
})();
